/**
 * SCRIPT mode, Welsh — THE DECK: what a Welsh learner reads, and in what order.
 *
 * Tom, 2026-09-24 (job #110), rebuilding Welsh Script mode after #75's message
 * thread was rejected: "It's just about matching the English to the Welsh /
 * Then the Welsh to the English / Use the same distinction model for all the
 * script learning journeys … And the content should be interleaved from the
 * seeds and the pod content probably / So we're learning to read the highest
 * value content immediately". No audio, anywhere.
 *
 * Welsh is written in letters the learner already reads, so the thing to learn
 * is not a letter: it is the course's own unit, a LEGO's Welsh, read and
 * recognised — "dw i isio" is one thing to read, as it is one thing to say.
 * Each LEGO's Welsh is a CHUNK, and each chunk is a distinction, exactly as a
 * character is on the character scripts. A seed or a pod line carries the
 * chunks it is tiled from (longest LEGO first, left to right; a word no LEGO
 * covers is a chunk of its own). The ITEMS are the course's own English/Welsh
 * pairs, never a gloss made up here:
 *
 *   L — a LEGO: the course's own unit, one English intention to one Welsh
 *       form (the course is ZUT-clean, so each English has exactly one Welsh).
 *   S — a seed sentence, after the LEGOs it is built from.
 *   P — a line of the Welsh pod-1 story, at the point the course has already
 *       opened most of its words.
 *   U — a USE phrase of the course's own (job #636, r-2026-10-29: Build covers
 *       the course first — seeds, LEGOs, phrases, pod), at most USE_PER_LEGO per
 *       LEGO, the shortest first; on a deck only where the builder is given them.
 *   O — OUT AND ABOUT (job #149): street Welsh the course never teaches —
 *       signs, station and shop wording, menus, machines, chat and email
 *       phrases — from job #145's hand lists (tools/script-corpus/cym/
 *       hand-lists.cym.json), each tagged north, south or both and kept only
 *       on the deck of its own variety. Read, found and typed exactly as a
 *       course item is; no audio. A street item never displaces the course:
 *       it is ranked after every course item for the one-Welsh-per-English
 *       rule, so where the two disagree the course's Welsh stands.
 *
 * HIGHEST VALUE FIRST (the ranking is this file's one taste call; see
 * docs/DECISIONS.md, job #110):
 *   1. Seeds in course order — the earliest seeds carry the most frequent
 *      words, which is why the course is ordered that way. Each seed's LEGOs
 *      come first, then the seed itself, so the sentence arrives readable.
 *   2. A pod line slots in after the first seed at which at least
 *      POD_READY of its word tokens are already on the deck — at most one
 *      pod line per seed, so the two sources interleave rather than clump.
 *      Among the lines ready at that point, the one whose words recur most
 *      across seeds and pod together goes first.
 *   3. Pod lines never that ready follow the seeds, most-covered first.
 *
 * ONE WELSH PER ENGLISH, AND ONE ENGLISH PER WELSH, across the whole deck, in
 * ranked order: the first item to claim either side keeps it. That is ZUT on
 * the deck — a learner shown an English never has two right Welsh answers, and
 * no card can carry two choices that read the same.
 *
 * LEFT OUT, never mended: an item with a parenthesis in it (no explanation in
 * the course, ever), and an item longer than MAX_WORDS words, which cannot sit
 * as a tappable choice on a phone.
 *
 * THE ORDER, given an out-and-about word table: the mixed order of
 * welshOrder.ts (job #149) over everything above. Without one, the course
 * order this comment describes.
 *
 * Pure: rows in, deck out. Run offline by tools/script-welsh/build-deck.mjs;
 * the deck ships as a static file (public/script-welsh/<course>.json).
 */

import { blendUsefulness, orderByUsefulness, radicals as welshRadicals } from './welshOrder'
import { contextIndex, glossHolds, scoreGloss, type ContextRow } from './glossInContext'

export type WelshItemKind = 'L' | 'S' | 'P' | 'O' | 'U'
export type WelshVariety = 'north' | 'south' | 'both'

export interface WelshItem {
  /** Progress-row id: `L:<seed>.<lego>`, `S:<seed>`, `U:<seed>.<lego>.<n>`, `P:<pod line>`, `O:<its Welsh, words joined by ->`. Stable across rebuilds. */
  id: string
  kind: WelshItemKind
  /** The Welsh, as shown. */
  cy: string
  /** The English, as shown. */
  en: string
  /** The Welsh words it carries, once each, in order — for ranking and foil distance. */
  words: string[]
  /** The chunks it is read as, once each, in order: its own Welsh for a LEGO, its tiling for a seed or pod line. */
  chunks: string[]
  /** An out-and-about item's variety and the list it came from (road_signs, chat_set_phrases …). */
  variety?: WelshVariety
  where?: string
  /**
   * The course's own clip of this Welsh, as an audio ref (`<uuid>` or `<uuid>.v<N>`),
   * for the play-the-word button (Tom 2026-09-27, job #475). Welsh courses only; absent
   * where the course has no clip, and then the item shows no button. See attachWelshAudio.
   */
  audio?: string
}

export interface WelshDeckFile {
  course: string
  version: number
  builtAt: string
  /** The pod the P items come from, or null for a course with no pod-1. */
  pod: string | null
  /** 'course' — course order with pod lines slotted in (job #110); 'mixed' — half course, half out and about (job #149). */
  order?: 'course' | 'mixed'
  items: WelshItem[]
}

export interface SeedRow { seed_number: number; known_text: string | null; target_text: string | null }
export interface LegoRow { seed_number: number; lego_index: number; known_text: string | null; target_text: string | null }
/** A USE phrase of the course's own (course_practice_phrases, phrase_role 'use'). */
export interface PhraseRow { seed_number: number; lego_index: number; known_text: string | null; target_text: string | null; phrase_role?: string | null }
export interface PodRow { global_order: number; known_text: string | null; target_text: string | null }
/** A street item. `variety` is Welsh's north/south tag; an Irish deck's street rows are already narrowed to its dialect and carry none. */
export interface StreetRow { list: string; cy: string; en: string; variety?: WelshVariety }

export interface WelshDeckOptions {
  /** Out-and-about items, already narrowed to the course's variety. */
  street?: readonly StreetRow[]
  /** Out-and-about word usefulness per million; given, the deck is put in the mixed order (welshOrder.ts). */
  outAndAbout?: ReadonlyMap<string, number>
  /** The mutation fold the mixed order credits a word's family through: Welsh's unless given (Irish: irish.ts). */
  radicals?: (w: string) => Set<string>
  /**
   * The course's BUILD and USE phrases (never components). Given, a one-word LEGO
   * joins only where its gloss holds across the course, out of context (job #497,
   * glossInContext.ts): "que = who" is true in seed 163's sentence, not on a bare card.
   */
  context?: readonly ContextRow[]
  /**
   * The course's USE phrases (job #636). Given, each LEGO's shortest USE_PER_LEGO
   * join the deck as U items, after the seeds, LEGOs and pod lines claim their
   * English and their target, and before any out-and-about item.
   */
  phrases?: readonly PhraseRow[]
}

/** A choice longer than this many words does not sit as a tile on a phone. */
export const MAX_WORDS = 10
/** A pod line joins the deck once this share of its word tokens is already there. */
export const POD_READY = 0.7
/** A Build deck carries at most this many USE phrases per LEGO (job #636): the phone fetches the deck whole. */
export const USE_PER_LEGO = 2
/** USE, under its older name too (CourseDataProvider reads both). */
const USE_ROLES = new Set(['use', 'eternal_eligible'])

const APOS = /[’‘ʼ`]/g

/** The Welsh or English as shown: curly quotes straightened, the pod's pause marks gone, spacing tidied. */
export function cleanText(raw: string | null | undefined): string {
  const t = raw ?? ''
  return t
    .replace(APOS, "'")
    .replace(/…|\.\.\./g, ' ')
    .replace(/\s+([,.!?;:])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^["“”']+|["“”]+$/g, '')
    // Spanish pairs its marks (¿…? ¡…!): a text that opens one keeps its close, so it never reads half-punctuated (job #480).
    .replace(/[¿¡]/u.test(t) ? /[.;:,]+$/u : /[.!?;:,]+$/u, '')
    .trim()
}

/** One word as a distinction key: lower case, letters and inner apostrophes and hyphens only. */
export function wordKey(raw: string): string {
  return raw.toLowerCase().replace(APOS, "'").replace(/^[^\p{L}]+|[^\p{L}]+$/gu, '').replace(/[^\p{L}'-]/gu, '')
}

export const wordsOf = (text: string): string[] => text.split(/\s+/).map(wordKey).filter(Boolean)

/** A whole text as a comparison key: its words, in order. */
export const textKey = (text: string): string => wordsOf(text).join(' ')

/**
 * THE COURSE'S OWN CASING. The course stores seeds and LEGOs in lower case
 * with the proper nouns capitalised ("Cymraeg", "Sarah"); the pod stores
 * ordinary sentence case. So one rule for both: the first letter is lowered
 * unless that word is written capitalised somewhere in the data other than at
 * the start of a line — the course's own evidence that it is a name. Without
 * this, a capital letter alone would say which choice came from the pod.
 */
export function properNouns(texts: readonly string[]): Set<string> {
  const out = new Set<string>()
  for (const t of texts) {
    const ws = t.split(/\s+/)
    for (let i = 1; i < ws.length; i++) {
      const prev = ws[i - 1]
      if (/[.!?]$/.test(prev)) continue
      const w = ws[i].replace(/^[^\p{L}]+|[^\p{L}'-]+$/gu, '')
      if (/^\p{Lu}/u.test(w) && w !== 'I') out.add(w)
    }
  }
  out.add('I')
  return out
}

export function sentenceCase(text: string, proper: ReadonlySet<string>): string {
  const first = text.split(/\s+/)[0] ?? ''
  const bare = first.replace(/^[^\p{L}]+|[^\p{L}'-]+$/gu, '')
  if (!bare || proper.has(bare) || /^I'/.test(bare)) return text
  // An initialism keeps its capitals: GRMA, TBO, OK (job #462).
  if (bare.length > 1 && bare === bare.toUpperCase() && bare !== bare.toLowerCase()) return text
  const i = text.search(/\p{L}/u)
  return i < 0 ? text : text.slice(0, i) + text[i].toLowerCase() + text.slice(i + 1)
}

export interface DeckStats {
  legos: number
  seeds: number
  pod: number
  /** USE phrases on the deck (job #636). */
  phrases?: number
  street: number
  /** Pod lines placed among the seeds, and after them. */
  podInterleaved: number
  podAfter: number
  dropped: { parenthesis: number; tooLong: number; empty: number; sameWelsh: number; sameEnglish: number; inContextOnly?: number }
  words: number
  chunks: number
}

/** The longest a LEGO runs, in words — the widest window the tiling tries. */
const MAX_CHUNK_WORDS = 8

/**
 * TILE a run of words from the chunks on offer: at each word, the longest
 * chunk that starts there, else the word alone. Left to right, deterministic.
 */
export function tileChunks(words: readonly string[], lexicon: ReadonlySet<string>, maxLen = words.length): string[] {
  const out: string[] = []
  for (let i = 0; i < words.length;) {
    let took = 1
    for (let n = Math.min(maxLen, MAX_CHUNK_WORDS, words.length - i); n > 1; n--) {
      if (lexicon.has(words.slice(i, i + n).join(' '))) { took = n; break }
    }
    out.push(words.slice(i, i + took).join(' '))
    i += took
  }
  return out
}

export function buildWelshDeck(
  course: string,
  seeds: readonly SeedRow[],
  legos: readonly LegoRow[],
  pod: readonly PodRow[] = [],
  podId: string | null = null,
  opts: WelshDeckOptions = {},
): { deck: WelshDeckFile; stats: DeckStats } {
  const street = opts.street ?? []
  const stats: DeckStats = {
    legos: 0, seeds: 0, pod: 0, phrases: 0, street: 0, podInterleaved: 0, podAfter: 0,
    dropped: { parenthesis: 0, tooLong: 0, empty: 0, sameWelsh: 0, sameEnglish: 0 }, words: 0, chunks: 0,
  }
  const proper = properNouns([
    ...seeds.flatMap(s => [cleanText(s.target_text), cleanText(s.known_text)]),
    ...legos.flatMap(l => [cleanText(l.target_text), cleanText(l.known_text)]),
    ...pod.flatMap(p => [cleanText(p.target_text), cleanText(p.known_text)]),
    ...street.flatMap(r => [cleanText(r.cy), cleanText(r.en)]),
  ])
  const make = (id: string, kind: WelshItemKind, cyRaw: string | null, enRaw: string | null): WelshItem | null => {
    const cy = sentenceCase(cleanText(cyRaw), proper), en = sentenceCase(cleanText(enRaw), proper)
    if (!cy || !en) { stats.dropped.empty++; return null }
    if (/[()]/.test(cy + en)) { stats.dropped.parenthesis++; return null }
    const all = wordsOf(cy)
    if (!all.length || all.length > MAX_WORDS || wordsOf(en).length > MAX_WORDS + 4) { stats.dropped.tooLong++; return null }
    return { id, kind, cy, en, words: [...new Set(all)], chunks: [] }
  }

  // ONE WORD, ONE MEANING: a one-word LEGO whose gloss is only true in its sentence stays in the spoken course.
  const ctx = opts.context ? contextIndex([...legos, ...seeds, ...pod, ...opts.context], opts.radicals ?? welshRadicals) : null
  if (ctx) stats.dropped.inContextOnly = 0
  const standsAlone = (it: WelshItem): boolean => {
    if (!ctx || it.words.length !== 1) return true
    if (glossHolds(scoreGloss(it.cy, it.en, ctx))) return true
    stats.dropped.inContextOnly = (stats.dropped.inContextOnly ?? 0) + 1
    return false
  }

  // THE SEED SPINE: each seed's LEGOs, then the seed.
  const legosBySeed = new Map<number, LegoRow[]>()
  for (const l of legos) {
    const group = legosBySeed.get(l.seed_number)
    if (group) group.push(l)
    else legosBySeed.set(l.seed_number, [l])
  }
  const spine: WelshItem[][] = []
  for (const s of [...seeds].sort((a, b) => a.seed_number - b.seed_number)) {
    const group: WelshItem[] = []
    for (const l of (legosBySeed.get(s.seed_number) ?? []).sort((a, b) => a.lego_index - b.lego_index)) {
      const it = make(`L:${l.seed_number}.${l.lego_index}`, 'L', l.target_text, l.known_text)
      if (it && standsAlone(it)) group.push(it)
    }
    const it = make(`S:${s.seed_number}`, 'S', s.target_text, s.known_text)
    if (it) group.push(it)
    spine.push(group)
  }
  const podItems = [...pod].sort((a, b) => a.global_order - b.global_order)
    .map(p => make(`P:${p.global_order}`, 'P', p.target_text, p.known_text))
    .filter((x): x is WelshItem => !!x)

  // Scoring revisits pod lines for every seed group and sort comparison.
  // Keep token multiplicity (item.words is deduplicated), local to this build.
  const scoringTokens = new Map<WelshItem, string[]>()
  const tokensOf = (it: WelshItem): string[] => {
    let tokens = scoringTokens.get(it)
    if (!tokens) {
      tokens = wordsOf(it.cy)
      scoringTokens.set(it, tokens)
    }
    return tokens
  }

  // How often each word turns up across seeds and pod together — the recurrence a pod line is valued by.
  const freq = new Map<string, number>()
  for (const it of [...spine.flat().filter(i => i.kind === 'S'), ...podItems]) for (const w of tokensOf(it)) freq.set(w, (freq.get(w) ?? 0) + 1)
  // Frequencies stay fixed while seed groups repeatedly compare ready lines.
  const values = new Map<WelshItem, number>()
  const value = (it: WelshItem): number => {
    const cached = values.get(it)
    if (cached !== undefined) return cached
    const ws = tokensOf(it)
    const score = ws.reduce((a, w) => a + (freq.get(w) ?? 0), 0) / ws.length
    values.set(it, score)
    return score
  }

  const ranked: WelshItem[] = []
  const onDeck = new Set<string>()
  const coverage = (it: WelshItem) => { const ws = tokensOf(it); return ws.filter(w => onDeck.has(w)).length / ws.length }
  const pending = [...podItems]
  const push = (it: WelshItem) => { ranked.push(it); for (const w of it.words) onDeck.add(w) }
  for (const group of spine) {
    for (const it of group) push(it)
    const ready = pending.filter(p => coverage(p) >= POD_READY)
    if (!ready.length) continue
    const best = ready.reduce((a, p) => (value(p) > value(a) ? p : a))
    pending.splice(pending.indexOf(best), 1)
    push(best)
    stats.podInterleaved++
  }
  // onDeck and freq stay fixed during this sort: score each remaining line
  // once rather than walking its tokens again for every comparison.
  const remaining = pending.map(item => ({ item, coverage: coverage(item), value: value(item) }))
  remaining.sort((a, b) => b.coverage - a.coverage || b.value - a.value)
  for (const { item } of remaining) { push(item); stats.podAfter++ }
  // THE COURSE'S OWN PHRASES (job #636): USE only, the shortest per LEGO, after every seed, LEGO and pod line.
  const useByLego = new Map<string, PhraseRow[]>()
  for (const r of opts.phrases ?? []) {
    if (!USE_ROLES.has((r.phrase_role ?? 'use').toLowerCase())) continue
    const k = `${r.seed_number}.${r.lego_index}`
    useByLego.set(k, [...(useByLego.get(k) ?? []), r])
  }
  const legoKeys = [...useByLego.keys()].sort((a, b) => {
    const [as, al] = a.split('.').map(Number), [bs, bl] = b.split('.').map(Number)
    return as - bs || al - bl
  })
  for (const k of legoKeys) {
    const rows = useByLego.get(k)!
      .map(r => ({ r, n: wordsOf(cleanText(r.target_text)).length, t: cleanText(r.target_text) }))
      .sort((a, b) => a.n - b.n || (a.t < b.t ? -1 : a.t > b.t ? 1 : 0))
    let n = 0
    for (const { r } of rows) {
      if (n >= USE_PER_LEGO) break
      const it = make(`U:${k}.${n + 1}`, 'U', r.target_text, r.known_text)
      if (it) { ranked.push(it); n++ }
    }
  }
  // OUT AND ABOUT, after every course item, so the course keeps its Welsh wherever the two disagree.
  for (const r of street) {
    const cy = cleanText(r.cy)
    const it = make(`O:${textKey(cy).replace(/ /g, '-')}`, 'O', r.cy, r.en)
    if (it) ranked.push(r.variety ? { ...it, variety: r.variety, where: r.list } : { ...it, where: r.list })
  }

  // ONE WELSH PER ENGLISH AND ONE ENGLISH PER WELSH, first claim wins.
  const seenCy = new Set<string>(), seenEn = new Set<string>()
  const items: WelshItem[] = []
  for (const it of ranked) {
    const cy = textKey(it.cy), en = textKey(it.en).toLowerCase()
    if (seenCy.has(cy)) { stats.dropped.sameWelsh++; continue }
    if (seenEn.has(en)) { stats.dropped.sameEnglish++; continue }
    seenCy.add(cy); seenEn.add(en)
    items.push(it)
    if (it.kind === 'L') stats.legos++
    else if (it.kind === 'S') stats.seeds++
    else if (it.kind === 'P') stats.pod++
    else if (it.kind === 'U') stats.phrases = (stats.phrases ?? 0) + 1
    else stats.street++
  }
  // THE CHUNKS: a LEGO is its own; everything else is tiled from the LEGOs on the deck.
  const lexicon = new Set(items.filter(i => i.kind === 'L').map(i => textKey(i.cy)))
  // No window wider than an available LEGO can match. Share the bound across
  // all sentences rather than probing impossible multiword chunks at each word.
  let maxChunkWords = 1
  for (const chunk of lexicon) maxChunkWords = Math.max(maxChunkWords, chunk.split(' ').length)
  for (const it of items) it.chunks = it.kind === 'L' ? [textKey(it.cy)] : [...new Set(tileChunks(wordsOf(it.cy), lexicon, maxChunkWords))]
  stats.words = new Set(items.flatMap(i => i.words)).size
  stats.chunks = new Set(items.flatMap(i => i.chunks)).size
  const mixed = opts.outAndAbout ? orderByUsefulness(items, blendUsefulness(items, opts.outAndAbout, opts.radicals)).map(o => o.item) : items
  return {
    deck: { course, version: opts.outAndAbout ? 2 : 1, builtAt: new Date().toISOString(), pod: podItems.length ? podId : null, order: opts.outAndAbout ? 'mixed' : 'course', items: mixed },
    stats,
  }
}

/**
 * PLAY THE WORD (Tom 2026-09-27, job #475): "Welsh Script mode gets a play-the-word
 * button that plays the Welsh audio for the item shown … Use EXISTING course clips
 * only … items with no clip simply show no button". The one exception to #110's
 * "no audio", and Welsh only.
 *
 * Which clip: the one the course itself plays for that row — the LEGO's, the
 * seed's or the pod line's own target link, looked up by item id — and only where
 * a row has no link, a clip of the course's own whose text is exactly this Welsh.
 * The course's own clips only, so a north deck plays north Welsh and a south deck
 * south. Nothing is ever rendered for this.
 */
export function attachWelshAudio(
  items: WelshItem[],
  linked: ReadonlyMap<string, string>,
  byText: ReadonlyMap<string, string>,
): { withAudio: number; linked: number; byText: number; total: number } {
  let viaLink = 0, viaText = 0
  for (const it of items) {
    const ref = linked.get(it.id)
    if (ref) { it.audio = ref; viaLink++; continue }
    const t = byText.get(textKey(it.cy))
    if (t) { it.audio = t; viaText++ } else delete it.audio
  }
  return { withAudio: viaLink + viaText, linked: viaLink, byText: viaText, total: items.length }
}

/**
 * A TOPIC POD'S OWN DECK (job #103; Tom 2026-10-28: "Italian has the environment
 * pod as well, so that could be used as well?"). Advanced pods are by taste
 * (r-2026-09-30), so a topic pod is never mixed into the course's main deck: it
 * is a deck of its own that a learner opts into (welshScript.ts TOPIC_DECKS).
 *
 * The builder runs exactly as for the main deck — the course's seed spine with
 * the topic pod in pod-1's place, so its lines are ordered by the same readiness
 * and the same mixed order — and this keeps what the topic deck is: the pod's
 * lines, and the LEGOs they are built from (the Build card tiles a line only
 * from LEGOs on its own deck, welshBuild.buildIndex). Seeds and everyday items
 * stay on the main deck.
 *
 * A line's id carries its pod's slug, `P:<slug>:<line>`: progress rows are kept
 * per course and item, and pod-1's `P:<line>` must never be credited for a
 * different pod's line. A LEGO keeps its `L:` id — reading it here is reading it.
 */
export function topicPodDeck(deck: WelshDeckFile, slug: string): WelshDeckFile {
  const lines = deck.items.filter(i => i.kind === 'P')
  const used = new Set(lines.flatMap(i => i.chunks))
  const items = deck.items
    .filter(i => i.kind === 'P' || (i.kind === 'L' && used.has(i.chunks[0])))
    .map(i => (i.kind === 'P' ? { ...i, id: `P:${slug}:${i.id.slice(2)}` } : i))
  return { ...deck, items }
}
